import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Typography,
  Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { SelectChangeEvent } from '@mui/material/Select';
import CommonPagination from '../../common/Pagination';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { InventoryRecordsService } from '../../../api/services/InventoryRecordsService';
import type { InventoryRecordItemResponseDto } from '../../../api/models/InventoryRecordItemResponseDto';
import type { InventoryRecordLabelItemDto } from '../../../api/models/InventoryRecordLabelItemDto';
import {
  openInventoryLabelCalibrationSheet,
  printInventoryLabels,
} from '../../../utils/printInventoryLabels';
import { formatProductCodeUniqueForDisplay } from '../../../utils/formatProductCodeUniqueDisplay';
import { InventoryLabelPreviewMini } from '../InventoryLabelPreviewMini';
import * as S from './InventoryLabelPrintDrawer.styles';

const LABEL_PRINT_ITEMS_PER_PAGE_OPTIONS = [5, 10, 15, 20, 25];
/** Siempre al abrir / recargar el flujo de impresión de un registro. */
const LABEL_PRINT_DEFAULT_PAGE_SIZE = 10;

const DRAWER_ALERT_BASE_SX = {
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box' as const,
  bgcolor: 'transparent',
  '& .MuiAlert-message': { overflowWrap: 'break-word' as const },
};

function drawerAlertWarningSx(theme: Theme) {
  return {
    ...DRAWER_ALERT_BASE_SX,
    color: theme.palette.mode === 'dark' ? theme.palette.warning.light : '#B45309',
    borderColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.55 : 0.65),
    '& .MuiAlert-icon': { color: theme.palette.warning.main },
  };
}

function drawerAlertInfoSx(theme: Theme) {
  return {
    ...DRAWER_ALERT_BASE_SX,
    color: theme.palette.text.primary,
    borderColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.5 : 0.45),
    '& .MuiAlert-icon': { color: theme.palette.info.light },
  };
}

function drawerAlertErrorSx(theme: Theme) {
  return {
    ...DRAWER_ALERT_BASE_SX,
    color: theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.dark,
    borderColor: alpha(theme.palette.error.main, 0.45),
    '& .MuiAlert-icon': { color: theme.palette.error.light },
  };
}

export interface LabelPrintItem {
  warehouseInventoryProductId: number;
  inventoryRecordItemId: number | null;
  catalogProductCode: string;
  catalogProductId: number | null;
  productCodeUnique: string;
  productType: string;
  materialLabel: string;
  salePrice: number;
  weightGrams: number | null;
  diameterSize: string | null;
  workshopCode: string | null;
}

export interface InventoryLabelPrintModalProps {
  open: boolean;
  onClose: () => void;
  recordId: number | null;
  recordCode?: string;
}

const TOTAL_STEPS = 3;

const STEP_TITLES = ['Selección de piezas', 'Vista previa e impresión por lote', 'Confirmación'];

const ORPHAN_LINE_KEY = -1;

function normCode(s: string | undefined | null): string {
  return (s ?? '').trim().toLowerCase();
}

function normalizeLineId(id: number | string | undefined): number {
  const n = Number(id);
  return Number.isFinite(n) ? n : NaN;
}

/** Nullable numeric ids arrive as numbers or numeric strings depending on the driver. */
function toOptionalId(value: number | string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function toOptionalText(value: string | null | undefined): string | null {
  const text = (value ?? '').trim();
  return text === '' ? null : text;
}

function toLabelPrintItem(dto: InventoryRecordLabelItemDto): LabelPrintItem {
  return {
    warehouseInventoryProductId: dto.warehouseInventoryProductId,
    inventoryRecordItemId: toOptionalId(dto.inventoryRecordItemId),
    catalogProductCode: dto.catalogProductCode ?? '',
    catalogProductId: toOptionalId(dto.catalogProductId),
    productCodeUnique: dto.productCodeUnique,
    productType: dto.productType,
    materialLabel: dto.materialLabel,
    salePrice: dto.salePrice,
    weightGrams: dto.weightGrams,
    diameterSize: toOptionalText(dto.diameterSize),
    workshopCode: toOptionalText(dto.workshopCode),
  };
}

function labelBelongsToLine(label: LabelPrintItem, line: InventoryRecordItemResponseDto): boolean {
  const lineId = normalizeLineId(line.id);
  if (Number.isNaN(lineId)) return false;

  if (label.inventoryRecordItemId != null) {
    return label.inventoryRecordItemId === lineId;
  }

  const labelCode = normCode(label.catalogProductCode);
  const lineCode = normCode(line.product?.code);
  if (labelCode !== '' && lineCode !== '' && labelCode === lineCode) return true;

  const catalogId = label.catalogProductId;
  if (catalogId == null) return false;
  return (
    Number(catalogId) === Number(line.productId) ||
    (line.product?.id != null && Number(catalogId) === Number(line.product.id))
  );
}

function setHasAll(selected: Set<number>, ids: number[]): boolean {
  return ids.length > 0 && ids.every((id) => selected.has(id));
}

function setHasSome(selected: Set<number>, ids: number[]): boolean {
  return ids.some((id) => selected.has(id));
}

function toggleIdsInSet(prev: Set<number>, ids: number[], select: boolean): Set<number> {
  const next = new Set(prev);
  for (const id of ids) {
    if (select) next.add(id);
    else next.delete(id);
  }
  return next;
}

const InventoryLabelPrintModal: React.FC<InventoryLabelPrintModalProps> = ({
  open,
  onClose,
  recordId,
  recordCode: recordCodeProp,
}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<LabelPrintItem[]>([]);
  const [recordLines, setRecordLines] = useState<InventoryRecordItemResponseDto[]>([]);
  const [recordCode, setRecordCode] = useState('');
  /** Piezas a imprimir (warehouseInventoryProductId). */
  const [selectedPieceIds, setSelectedPieceIds] = useState<Set<number>>(new Set());
  /** Líneas expandidas para ver códigos únicos. */
  const [expandedLineIds, setExpandedLineIds] = useState<Set<number>>(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(LABEL_PRINT_DEFAULT_PAGE_SIZE);
  const [printing, setPrinting] = useState(false);
  const [printFeedback, setPrintFeedback] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setStep(1);
      setPrintFeedback(null);
      setPrinting(false);
      setExpandedLineIds(new Set());
    }
  }, [open]);

  const load = useCallback(async () => {
    if (recordId == null) return;
    setLoading(true);
    setError(null);
    setStep(1);
    setExpandedLineIds(new Set());
    try {
      const [labelRes, detailRes] = await Promise.all([
        InventoryRecordsService.inventoryRecordsControllerFindLabelPrintItems(Number(recordId)),
        InventoryRecordsService.inventoryRecordsControllerFindOneDetailed(String(recordId), false),
      ]);

      const rcRaw = labelRes.recordCode ?? detailRes.recordCode ?? recordCodeProp ?? '';
      const rc = typeof rcRaw === 'string' ? rcRaw : '';
      setRecordCode(rc);

      const list = Array.isArray(labelRes.items) ? labelRes.items : [];
      const mapped = list.map(toLabelPrintItem);
      setItems(mapped);

      const lines = detailRes.items ?? [];
      setRecordLines(lines);
      // Default: todas las piezas (comportamiento previo al marcar todas las líneas).
      setSelectedPieceIds(
        new Set(
          mapped
            .map((i) => i.warehouseInventoryProductId)
            .filter((id) => Number.isFinite(id) && id > 0),
        ),
      );
      setPage(1);
      setPageSize(LABEL_PRINT_DEFAULT_PAGE_SIZE);
    } catch (e: unknown) {
      console.error(e);
      setError('No se pudieron cargar los datos para etiquetas.');
      setItems([]);
      setRecordLines([]);
      setSelectedPieceIds(new Set());
    } finally {
      setLoading(false);
    }
  }, [recordId, recordCodeProp]);

  useEffect(() => {
    if (open && recordId != null) {
      load();
    }
  }, [open, recordId, load]);

  /**
   * Piezas agrupadas por línea de registro; huérfanas en ORPHAN_LINE_KEY.
   * Cada pieza se asigna a una sola línea: sin `inventoryRecordItemId` el
   * emparejamiento por código/catálogo puede coincidir con varias líneas.
   */
  const piecesByLineId = useMemo(() => {
    const lineIds = recordLines
      .map((line) => ({ line, lineId: normalizeLineId(line.id) }))
      .filter(({ lineId }) => !Number.isNaN(lineId));

    const map = new Map<number, LabelPrintItem[]>(lineIds.map(({ lineId }) => [lineId, []]));
    const orphans: LabelPrintItem[] = [];

    for (const piece of items) {
      const match = lineIds.find(({ line }) => labelBelongsToLine(piece, line));
      if (match == null) {
        orphans.push(piece);
        continue;
      }
      map.get(match.lineId)?.push(piece);
    }

    if (orphans.length > 0) {
      map.set(ORPHAN_LINE_KEY, orphans);
    }
    return map;
  }, [items, recordLines]);

  const expandableLineIds = useMemo(() => {
    const ids: number[] = [];
    for (const [lineId, pieces] of piecesByLineId) {
      if (pieces.length > 0) ids.push(lineId);
    }
    return ids;
  }, [piecesByLineId]);

  const allPieceIds = useMemo(
    () =>
      items.map((i) => i.warehouseInventoryProductId).filter((id) => Number.isFinite(id) && id > 0),
    [items],
  );

  const filteredItems = useMemo(() => {
    if (selectedPieceIds.size === 0) return [];
    return items.filter((lbl) => selectedPieceIds.has(lbl.warehouseInventoryProductId));
  }, [items, selectedPieceIds]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize) || 1);
  const safePage = Math.min(page, totalPages);
  const pageSlice = useMemo(() => {
    const p = Math.min(Math.max(1, page), totalPages);
    const start = (p - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, page, pageSize, totalPages]);

  const allSelected = setHasAll(selectedPieceIds, allPieceIds);
  const someSelected = setHasSome(selectedPieceIds, allPieceIds);

  const isFullyExpanded =
    expandableLineIds.length > 0 && expandableLineIds.every((id) => expandedLineIds.has(id));

  const canProceedStep1 = filteredItems.length > 0;
  const canPrint = filteredItems.length > 0 && pageSlice.length > 0;

  const progressPercent = Math.round((step / TOTAL_STEPS) * 100);

  const togglePiece = (pieceId: number) => {
    if (!Number.isFinite(pieceId) || pieceId <= 0) return;
    setSelectedPieceIds((prev) => {
      const next = new Set(prev);
      if (next.has(pieceId)) next.delete(pieceId);
      else next.add(pieceId);
      return next;
    });
    setPage(1);
  };

  const toggleLinePieces = (lineId: number) => {
    const pieces = piecesByLineId.get(lineId) ?? [];
    const ids = pieces.map((p) => p.warehouseInventoryProductId);
    if (ids.length === 0) return;
    const selectAll = !setHasAll(selectedPieceIds, ids);
    setSelectedPieceIds((prev) => toggleIdsInSet(prev, ids, selectAll));
    setPage(1);
  };

  const onSelectAllHeaderChange = () => {
    if (allSelected) {
      setSelectedPieceIds(new Set());
    } else {
      setSelectedPieceIds(new Set(allPieceIds));
    }
    setPage(1);
  };

  const toggleLineExpansion = (lineId: number) => {
    setExpandedLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const handleToggleExpandAll = () => {
    setExpandedLineIds((prev) => {
      if (expandableLineIds.every((id) => prev.has(id))) {
        return new Set();
      }
      return new Set(expandableLineIds);
    });
  };

  const handleLabelItemsPerPageChange = (e: SelectChangeEvent<unknown>) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  const handlePrintBatch = async () => {
    if (!canPrint || printing) return;
    setPrinting(true);
    setPrintFeedback(null);
    try {
      const result = await printInventoryLabels(
        pageSlice.map((r) => ({
          productCodeUnique: r.productCodeUnique,
          materialLabel: r.materialLabel,
          salePrice: r.salePrice,
          weightGrams: r.weightGrams,
          diameterSize: r.diameterSize,
          workshopCode: r.workshopCode,
        })),
      );
      if (result.ok) {
        if (result.channel === 'browser-print') {
          setPrintFeedback('Etiquetas enviadas (Zebra Browser Print).');
        } else if (result.channel === 'local-agent') {
          setPrintFeedback('Etiquetas enviadas (agente local CUPS).');
        } else {
          setPrintFeedback(
            'Diálogo de impresión abierto. En Chrome: quite "Encabezado y pie de página", márgenes Ninguno, papel 88×10 mm (o el tamaño de su rollo), impresora GC420t.',
          );
        }
      } else {
        setPrintFeedback(result.message);
      }
    } finally {
      setPrinting(false);
    }
  };

  const displayCode = recordCode || recordCodeProp || '';

  const renderLineRow = (
    lineKey: number,
    title: string,
    subtitle: string,
    pieces: LabelPrintItem[],
    showBottomBorder: boolean,
  ) => {
    const pieceIds = pieces.map((p) => p.warehouseInventoryProductId);
    const lineAllSelected = setHasAll(selectedPieceIds, pieceIds);
    const lineSomeSelected = setHasSome(selectedPieceIds, pieceIds);
    const selectedCount = pieceIds.filter((id) => selectedPieceIds.has(id)).length;
    const isExpanded = expandedLineIds.has(lineKey);
    const hasPieces = pieces.length > 0;
    const chipLabel =
      hasPieces && selectedCount !== pieces.length
        ? `${selectedCount}/${pieces.length} pza.`
        : `${hasPieces ? pieces.length : 0} pza.`;

    return (
      <Box
        key={lineKey}
        sx={{
          borderBottom: showBottomBorder ? '1px solid' : 'none',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            '&:hover': { bgcolor: 'action.hover' },
            transition: 'background-color 0.2s',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Checkbox
              checked={hasPieces && lineAllSelected}
              indeterminate={lineSomeSelected && !lineAllSelected}
              onChange={() => toggleLinePieces(lineKey)}
              disabled={!hasPieces}
              size="small"
              inputProps={{
                'aria-label': `Seleccionar todas las piezas de ${title}`,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: 'text.primary', display: 'block' }}
                noWrap
              >
                {title}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {subtitle}
                {!hasPieces ? ' · sin etiquetas para esta línea' : ''}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
            <Chip
              label={chipLabel}
              size="small"
              sx={{
                bgcolor: 'action.selected',
                color: 'text.primary',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            />
            {hasPieces ? (
              <Button
                onClick={() => toggleLineExpansion(lineKey)}
                size="small"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'text.secondary',
                  textTransform: 'none',
                  px: 1,
                  py: 0.5,
                  '&:hover': {
                    color: 'text.primary',
                    bgcolor: 'action.hover',
                  },
                  transition: 'all 0.2s',
                }}
                title={isExpanded ? 'Ocultar piezas' : 'Ver piezas'}
              >
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {isExpanded ? 'Ocultar' : 'Ver'} piezas
                </Typography>
                <Box
                  component="svg"
                  xmlns="http://www.w3.org/2000/svg"
                  sx={{
                    width: '1rem',
                    height: '1rem',
                    transition: 'transform 0.2s',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </Box>
              </Button>
            ) : null}
          </Box>
        </Box>

        {isExpanded && hasPieces ? (
          <Box
            sx={{
              bgcolor: 'action.hover',
              borderTop: '1px solid',
              borderColor: 'divider',
              px: 3,
              py: 1.5,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
              Piezas ({pieces.length})
            </Typography>
            <Box
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              {pieces.map((piece, pieceIdx) => {
                const checked = selectedPieceIds.has(piece.warehouseInventoryProductId);
                return (
                  <Box
                    key={piece.warehouseInventoryProductId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.75,
                      borderBottom: pieceIdx < pieces.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      onChange={() => togglePiece(piece.warehouseInventoryProductId)}
                      size="small"
                      inputProps={{
                        'aria-label': `Seleccionar pieza ${piece.productCodeUnique}`,
                      }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                        noWrap
                      >
                        {formatProductCodeUniqueForDisplay(piece.productCodeUnique)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {piece.productType}
                        {piece.materialLabel ? ` · ${piece.materialLabel}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : null}
      </Box>
    );
  };

  const renderStep1 = () => {
    const orphanPieces = piecesByLineId.get(ORPHAN_LINE_KEY) ?? [];
    const lineEntries = recordLines
      .map((line, idx) => {
        const lineId = normalizeLineId(line.id);
        return {
          line,
          lineId: Number.isNaN(lineId) ? NaN : lineId,
          idx,
        };
      })
      .filter((e) => !Number.isNaN(e.lineId));

    const totalRows = lineEntries.length + (orphanPieces.length > 0 ? 1 : 0);

    return (
      <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
        <Box
          sx={{
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 1,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              bgcolor: 'action.hover',
              px: 3,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Productos ({recordLines.length})
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              {expandableLineIds.length > 0 ? (
                <Button
                  size="small"
                  onClick={handleToggleExpandAll}
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                >
                  {isFullyExpanded ? 'Ocultar todo' : 'Mostrar todo'}
                </Button>
              ) : null}
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onChange={onSelectAllHeaderChange}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Todos</Typography>}
              />
            </Box>
          </Box>

          {recordLines.length === 0 && orphanPieces.length === 0 ? (
            <Box sx={{ px: 3, py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No hay líneas de producto en este registro.
              </Typography>
            </Box>
          ) : (
            <>
              {lineEntries.map(({ line, lineId }, rowIdx) => {
                const pieces = piecesByLineId.get(lineId) ?? [];
                return renderLineRow(
                  lineId,
                  line.product?.name || `Producto ${line.productId}`,
                  line.product?.code || '-',
                  pieces,
                  rowIdx < totalRows - 1,
                );
              })}
              {orphanPieces.length > 0
                ? renderLineRow(
                    ORPHAN_LINE_KEY,
                    'Otras piezas',
                    'Sin línea de producto asociada',
                    orphanPieces,
                    false,
                  )
                : null}
            </>
          )}
        </Box>
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Etiquetas que se imprimirán con la selección actual:{' '}
            <strong>{filteredItems.length}</strong>
            {allPieceIds.length > 0 ? ` de ${allPieceIds.length}` : ''}
          </Typography>
        </Box>
        {selectedPieceIds.size === 0 && allPieceIds.length > 0 && (
          <Alert
            severity="warning"
            variant="outlined"
            sx={(theme) => ({ ...drawerAlertWarningSx(theme), mt: 1.5 })}
          >
            Seleccione al menos una pieza para continuar.
          </Alert>
        )}
      </Box>
    );
  };

  const renderStep2 = () => (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'stretch',
      }}
    >
      {filteredItems.length > 0 ? (
        <Box sx={{ mb: 1.5, flexShrink: 0 }}>
          <CommonPagination
            currentPage={safePage}
            itemsPerPage={pageSize}
            totalItems={filteredItems.length}
            onPageChange={setPage}
            onItemsPerPageChange={handleLabelItemsPerPageChange}
            itemsPerPageOptions={LABEL_PRINT_ITEMS_PER_PAGE_OPTIONS}
            itemLabelPlural="etiquetas"
          />
        </Box>
      ) : null}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          alignItems: 'flex-start',
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          p: 1.5,
          bgcolor: 'action.hover',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {pageSlice.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No hay etiquetas en este lote.
          </Typography>
        ) : (
          pageSlice.map((row) => (
            <Box
              key={row.warehouseInventoryProductId}
              sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}
            >
              <InventoryLabelPreviewMini row={row} />
              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 220 }}>
                {row.productType}
              </Typography>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );

  const renderStep3 = () => (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        minHeight: 320,
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          bgcolor: 'action.hover',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Resumen antes de imprimir
        </Typography>
      </Box>
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="body2">
          <strong>Registro:</strong> {displayCode || '—'}
        </Typography>
        <Typography variant="body2">
          <strong>Total de etiquetas en la selección:</strong> {filteredItems.length}
        </Typography>
        <Typography variant="body2">
          <strong>Etiquetas por página (lote):</strong> {pageSize}
        </Typography>
        <Typography variant="body2">
          <strong>Lote a imprimir:</strong> página {safePage} de {totalPages}
        </Typography>
        <Typography variant="body2">
          <strong>En esta impresión:</strong> {pageSlice.length} etiqueta(s)
        </Typography>
        <Alert
          severity="info"
          variant="outlined"
          sx={(theme) => ({ ...drawerAlertInfoSx(theme), mt: 1 })}
        >
          Impresión: intenta envío silencioso; si no está disponible, abre el diálogo de Chrome. En
          Windows elija la GC420t (driver Zebra), papel personalizado <strong>88×10 mm</strong>,
          márgenes 0. No hace falta Browser Print en cada PC.
        </Alert>
        {printFeedback != null ? (
          <Alert
            severity={printFeedback.startsWith('Etiquetas enviadas') ? 'success' : 'warning'}
            variant="outlined"
            sx={(theme) =>
              printFeedback.startsWith('Etiquetas enviadas')
                ? drawerAlertInfoSx(theme)
                : drawerAlertWarningSx(theme)
            }
          >
            {printFeedback}
          </Alert>
        ) : null}
      </Box>
    </Box>
  );

  const renderMainContent = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            minHeight: 360,
          }}
        >
          <CircularProgress size={56} />
        </Box>
      );
    }
    if (error) {
      return (
        <Alert severity="error" variant="outlined" sx={(theme) => drawerAlertErrorSx(theme)}>
          {error}
        </Alert>
      );
    }
    if (items.length === 0) {
      return (
        <Alert severity="info" variant="outlined" sx={(theme) => drawerAlertInfoSx(theme)}>
          No hay piezas de almacén para imprimir en este registro (o el registro está cancelado).
        </Alert>
      );
    }

    switch (step) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return null;
    }
  };

  const handleFooterPrevious = () => {
    if (step === 1) {
      onClose();
    } else {
      setStep((s) => (s === 2 ? 1 : 2) as 1 | 2 | 3);
    }
  };

  const handleFooterNext = () => {
    if (step === 1 && canProceedStep1) setStep(2);
    else if (step === 2 && canPrint) setStep(3);
  };

  if (!open) {
    return null;
  }

  return (
    <S.StyledDrawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: S.labelPrintDrawerPaperSx }}
    >
      <S.DrawerContainer>
        <S.DrawerHeader>
          <S.DrawerTitle component="div">
            <ReceiptIcon sx={{ color: 'info.main' }} />
            Imprimir etiquetas
            {displayCode ? (
              <Box component="span" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                · {displayCode}
              </Box>
            ) : null}
          </S.DrawerTitle>
          <S.CloseButton onClick={onClose} aria-label="Cerrar">
            <CloseIcon />
          </S.CloseButton>
        </S.DrawerHeader>

        <S.ProgressContainer>
          <S.ProgressBar>
            <S.ProgressBarFill style={{ width: `${progressPercent}%` }} />
          </S.ProgressBar>
          <S.ProgressInfo>
            <span>
              Paso {step} de {TOTAL_STEPS} · {STEP_TITLES[step - 1]}
            </span>
            <span>{progressPercent}%</span>
          </S.ProgressInfo>
        </S.ProgressContainer>

        <S.ContentContainer>{renderMainContent()}</S.ContentContainer>

        <S.DrawerFooter>
          <S.PreviousButton variant="tonal" onClick={handleFooterPrevious} disabled={loading}>
            {step === 1 ? 'Cancelar' : 'Anterior'}
          </S.PreviousButton>
          <S.FooterButtonsContainer>
            {!error && !loading && items.length > 0 ? (
              <>
                {step < 3 ? (
                  <S.NextButton
                    variant="contained"
                    onClick={handleFooterNext}
                    disabled={(step === 1 && !canProceedStep1) || (step === 2 && !canPrint)}
                  >
                    Siguiente
                  </S.NextButton>
                ) : (
                  <>
                    {/* Measures the printer's offset on the real roll; see openInventoryLabelCalibrationSheet. */}
                    <Button
                      variant="outlined"
                      onClick={openInventoryLabelCalibrationSheet}
                      disabled={printing}
                      title="Imprime una regla en mm. La línea 68 debe caer justo al inicio de la pieza del código de barras."
                      sx={{ textTransform: 'none', borderRadius: '10px' }}
                    >
                      Imprimir calibración
                    </Button>
                    <S.ConfirmButton
                      variant="contained"
                      startIcon={
                        printing ? <CircularProgress size={18} color="inherit" /> : <PrintIcon />
                      }
                      onClick={() => {
                        void handlePrintBatch();
                      }}
                      disabled={!canPrint || printing}
                    >
                      {printing ? 'Imprimiendo…' : 'Imprimir etiquetas'}
                    </S.ConfirmButton>
                  </>
                )}
              </>
            ) : (
              <Button
                variant="outlined"
                onClick={onClose}
                sx={(theme) => ({
                  textTransform: 'none',
                  borderRadius: '10px',
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  backgroundColor: theme.palette.background.paper,
                  '&:hover': {
                    borderColor: theme.palette.text.secondary,
                    backgroundColor: theme.palette.action.hover,
                  },
                })}
              >
                Cerrar
              </Button>
            )}
          </S.FooterButtonsContainer>
        </S.DrawerFooter>
      </S.DrawerContainer>
    </S.StyledDrawer>
  );
};

export default InventoryLabelPrintModal;
